import Cookies from 'js-cookie'
import PropTypes from 'prop-types'
import React from 'react'

import {
    getJwt,
    getQuoteDesking,
    getQuoteDeskingPaymentOptions,
    getQuoteSummaries,
    postCatalogNewPaymentOptions,
    putQuoteVehicleCatalog,
    putQuoteVehicleListingNew,
    putQuoteVehicleListingUsed,
} from '../apis/showroom/showroom.service'
import { getCatalogVdp, getInventoryVehicleVdp } from '../apis/vehicles/vehicles.service'
import { payloadCatalogPaymentOptionTransform } from '../transforms/payloadPaymentOptions.transform'
import {
    payloadQuoteCatalogTransform,
    payloadQuoteNewTransform,
    payloadQuoteUsedTransform,
} from '../transforms/payloadQuote.transform'
import { quoteDeskingTransform } from '../transforms/quoteDesking.transform'
import { vehicleInventoryTransform } from '../transforms/vehicle.transform'
import { WindowProxy, getParamValue } from '../utils/handle-url.utils'

const initialState = {
    isFetching: false,
    jwt: '',
    paymentOptions: {},
    dealerInfos: {},
    quoteId: '',
    vehicleId: null,
    newCarTrimId: null,
    accountId: null,
    quotesInGarage: [],
    vehicle: {},
    quoteInfos: {},
    hasShowroomWidgets: false,
    calculatorCtaVersion: 'primary',
    category: 'inventory',
    isStandaloneVLP: false,
    isStandaloneVDP: false,
    selectedPurchaseMethod: 'cash',
    selectedTerm: 0,
    selectedFrequency: 0,
    selectedKmPerYearPlan: 0,
    calculatorView: 'modal',
}

const reducer = (state, action) => {
    switch (action.type) {
        case 'fetching': {
            return {
                ...state,
                isFetching: true,
            }
        }
        case 'fetchSucceed': {
            return {
                ...state,
                isFetching: false,
            }
        }
        case 'updateState': {
            return {
                ...state,
                ...action.state,
            }
        }
        case 'setJwt': {
            return {
                ...state,
                jwt: action.jwt,
            }
        }
        case 'setDealerInfos': {
            return {
                ...state,
                dealerInfos: { ...state.dealerInfos, ...action.dealerInfos },
            }
        }
        case 'setPaymentOptions': {
            return {
                ...state,
                paymentOptions: { ...state.paymentOptions, ...action.paymentOptions },
            }
        }
        case 'setHasShowroomWidgets': {
            return {
                ...state,
                hasShowroomWidgets: action.hasShowroomWidgets,
            }
        }
        case 'setVehicleCategory': {
            return {
                ...state,
                category: action.vehicleCategory,
            }
        }
        case 'setCalculatorTriggerState': {
            return {
                ...state,
                isStandaloneVLP: action.isStandaloneVLP,
            }
        }
        case 'setCalculatorVDPTriggerState': {
            return {
                ...state,
                isStandaloneVDP: action.isStandaloneVDP,
            }
        }
        case 'setSelectedPurchaseMethod': {
            return {
                ...state,
                selectedPurchaseMethod: action.selectedPurchaseMethod,
            }
        }
        case 'setSelectedTerm': {
            return {
                ...state,
                selectedTerm: action.selectedTerm,
            }
        }
        case 'setSelectedFrequency': {
            return {
                ...state,
                selectedFrequency: action.selectedFrequency,
            }
        }
        case 'setSelectedKmPerYearPlan': {
            return {
                ...state,
                selectedKmPerYearPlan: action.selectedKmPerYearPlan,
            }
        }
        case 'setCalculatorCtaVersion': {
            return {
                ...state,
                calculatorCtaVersion: action.calculatorCtaVersion,
            }
        }
        case 'setQuoteId': {
            return {
                ...state,
                quoteId: action.quoteId,
            }
        }
        case 'setVehicleId': {
            return {
                ...state,
                vehicleId: action.vehicleId,
            }
        }
        case 'setNewCarTrimId': {
            return {
                ...state,
                newCarTrimId: action.newCarTrimId,
            }
        }
        case 'setVehicleInventoryId': {
            return {
                ...state,
                vehicleInventoryId: action.vehicleInventoryId,
            }
        }
        case 'setAccountId': {
            return {
                ...state,
                accountId: action.accountId,
            }
        }
        case 'setQuotesInGarage': {
            return {
                ...state,
                quotesInGarage: action.quotesInGarage,
            }
        }
        case 'setVehicle': {
            return {
                ...state,
                vehicle: { ...state.vehicle, ...action.vehicle },
            }
        }
        case 'setQuoteInfos': {
            return {
                ...state,
                quoteInfos: { ...state.quoteInfos, ...action.quoteInfos },
            }
        }
        case 'setCalculatorView': {
            return {
                ...state,
                calculatorView: action.calculatorView,
            }
        }
        default:
            return state
    }
}

const Context = React.createContext(initialState)

const Provider = ({
    lang,
    location,
    orgId,
    orgUnitId,
    quoteId,
    vehicleId,
    newCarTrimId,
    vehicleInventoryId,
    hasShowroomWidgets,
    children,
    calculatorCtaVersion,
    vehicleCategory,
    isStandaloneVLP,
    isStandaloneVDP,
    selectedPurchaseMethod,
    selectedTerm,
    selectedFrequency,
    selectedKmPerYearPlan,
    calculatorView,
}) => {
    const [state, dispatch] = React.useReducer(reducer, initialState)
    const cookie = Cookies.get('auth-token')

    // Init dealer and quoteId info in State
    React.useEffect(() => {
        dispatch({ type: 'fetching' })

        dispatch({ type: 'setDealerInfos', dealerInfos: { lang, location, orgId, orgUnitId } })
        dispatch({ type: 'setQuoteId', quoteId: quoteId || '' })
        dispatch({ type: 'setVehicleId', vehicleId: parseInt(vehicleId) || null })
        dispatch({ type: 'setNewCarTrimId', newCarTrimId: parseInt(newCarTrimId) || null })
        dispatch({ type: 'setVehicleInventoryId', vehicleInventoryId: parseInt(vehicleInventoryId) || null })
        dispatch({ type: 'setHasShowroomWidgets', hasShowroomWidgets })
        dispatch({ type: 'setCalculatorCtaVersion', calculatorCtaVersion })
        dispatch({ type: 'setVehicleCategory', vehicleCategory })
        dispatch({ type: 'setCalculatorTriggerState', isStandaloneVLP })
        dispatch({ type: 'setCalculatorVDPTriggerState', isStandaloneVDP })
        dispatch({ type: 'setSelectedPurchaseMethod', selectedPurchaseMethod })
        dispatch({ type: 'setSelectedTerm', selectedTerm: parseInt(selectedTerm) })
        dispatch({ type: 'setSelectedFrequency', selectedFrequency: parseInt(selectedFrequency) })
        dispatch({ type: 'setSelectedKmPerYearPlan', selectedKmPerYearPlan: parseInt(selectedKmPerYearPlan) })
        dispatch({ type: 'setCalculatorView', calculatorView: calculatorView || 'modal' })

        dispatch({ type: 'fetchSucceed' })
    }, [])

    // Init token JWT info in State
    React.useEffect(() => {
        const fetchJWT = async () => {
            try {
                dispatch({ type: 'fetching' })

                const initJwt = cookie || (await getJwt())

                dispatch({ type: 'setJwt', jwt: initJwt.data || initJwt })

                dispatch({ type: 'fetchSucceed' })
            } catch (error) {
                console.error(error)
            }
        }

        if (!state.jwt) fetchJWT()
    }, [state.jwt])

    // Init quote summaries info in State
    React.useEffect(() => {
        const fetchQuoteSummaries = async () => {
            try {
                dispatch({ type: 'fetching' })

                // TODO: delete la condition ` || 64308`
                const accountId = window.CommonsGeneral?.AccountService?.getUserAccountId() || 190381

                const params = {
                    accountId,
                    lang,
                    location,
                    orgId: parseInt(orgId),
                    orgUnitId: parseInt(orgUnitId),
                    token: cookie,
                }

                const quotesInGarage = await getQuoteSummaries(params)

                dispatch({ type: 'setAccountId', accountId })
                dispatch({ type: 'setQuotesInGarage', quotesInGarage: quotesInGarage.data })

                dispatch({ type: 'fetchSucceed' })
            } catch (e) {
                console.error(e)
            }
        }

        if (cookie && state.jwt && Object.entries(state.dealerInfos).length && !state.accountId) fetchQuoteSummaries()
    }, [cookie, state.jwt, state.dealerInfos, state.accountId])

    // If vehicleId is in state so Extract vehicle inventory information and create quoteId
    React.useEffect(() => {
        const fetchVehicleInventory = async () => {
            try {
                dispatch({ type: 'fetching' })

                const params = {
                    vehicleId,
                    location,
                    orgId: parseInt(orgId),
                    orgUnitId: parseInt(orgUnitId),
                    lang,
                }

                const vehicleInfos = await getInventoryVehicleVdp(params)
                const { vehicle, paymentOptions } = vehicleInventoryTransform(vehicleInfos.data)

                const jwt = await getJwt()

                dispatch({ type: 'setVehicle', vehicle })

                if (vehicle.newVehicle) {
                    const payloadNew = payloadQuoteNewTransform(vehicle, paymentOptions, state.dealerInfos, jwt.data)
                    const quote = await putQuoteVehicleListingNew(payloadNew)

                    dispatch({ type: 'setQuoteId', quoteId: quote.data.id || '' })
                } else {
                    const payloadUsed = payloadQuoteUsedTransform(vehicle, paymentOptions, state.dealerInfos, jwt.data)
                    const quote = await putQuoteVehicleListingUsed(payloadUsed)

                    dispatch({ type: 'setQuoteId', quoteId: quote.data.id || '' })
                }

                dispatch({ type: 'fetchSucceed' })
            } catch (e) {
                console.error(e)
            }
        }
        // const fetchVehicleInventoryWithQuote = async () => {
        //     try {
        //         dispatch({ type: 'fetching' })

        //         const params = {
        //             vehicleId,
        //             location,
        //             orgId: parseInt(orgId),
        //             orgUnitId: parseInt(orgUnitId),
        //             lang,
        //         }

        //         const vehicleInfos = await getInventoryVehicleVdp(params)
        //         const { vehicle } = vehicleInventoryTransform(vehicleInfos.data)
        //         console.log('vehicle here', vehicle)
        //         dispatch({ type: 'setVehicle', vehicle })

        //         dispatch({ type: 'setQuoteId', quoteId: quoteId || '' })

        //         dispatch({ type: 'fetchSucceed' })
        //     } catch (e) {
        //         console.error(e)
        //     }
        // }
        if (state.vehicleId && !quoteId && state.jwt) fetchVehicleInventory()
        // if (vehicleId && quoteId && state.jwt) fetchVehicleInventoryWithQuote()
    }, [state.vehicleId, quoteId, state.jwt])

    // If newCarTrimId is in state so Extract vehicle catalog information and create quoteId
    React.useEffect(() => {
        const fetchVehicleCatalog = async () => {
            try {
                dispatch({ type: 'fetching' })

                const params = {
                    newCarTrimId,
                    location,
                    orgId: parseInt(orgId),
                    orgUnitId: parseInt(orgUnitId),
                    lang,
                }

                const vehicleInfos = await getCatalogVdp(params)

                const jwt = await getJwt()

                const payloadPaymentOptions = payloadCatalogPaymentOptionTransform(vehicleInfos.data, state.dealerInfos, jwt.data)

                const vehiclePaymentOption = await postCatalogNewPaymentOptions(payloadPaymentOptions)

                dispatch({ type: 'setVehicle', vehicle: vehiclePaymentOption.data.vehicles[0] })

                const payloadCatalog = payloadQuoteCatalogTransform(
                    vehiclePaymentOption.data.vehicles[0],
                    state.dealerInfos,
                    jwt.data
                )
                const quote = await putQuoteVehicleCatalog(payloadCatalog)

                dispatch({ type: 'setQuoteId', quoteId: quote.data.id || '' })

                dispatch({ type: 'fetchSucceed' })
            } catch (e) {
                console.error(e)
            }
        }

        if (state.newCarTrimId && !quoteId && state.jwt) fetchVehicleCatalog()
    }, [state.newCarTrimId, quoteId, state.jwt])

    // If quoteId is in state so Extract Quote information
    React.useEffect(() => {
        const fetchQuoteDesking = async () => {
            try {
                dispatch({ type: 'fetching' })

                const params = {
                    quoteId: state.quoteId,
                    lang,
                    location,
                    orgId: parseInt(orgId),
                    orgUnitId: parseInt(orgUnitId),
                    token: state.jwt,
                }

                const quote = await getQuoteDesking(params)
                const { vehicle, quoteInfos } = quoteDeskingTransform(quote.data)
                if (!state.vehicleId) {
                    dispatch({ type: 'setVehicle', vehicle })
                } else {
                    const params = {
                        vehicleId,
                        location,
                        orgId: parseInt(orgId),
                        orgUnitId: parseInt(orgUnitId),
                        lang,
                    }

                    const vehicleInfos = await getInventoryVehicleVdp(params)
                    const { vehicle } = vehicleInventoryTransform(vehicleInfos.data)
                    dispatch({ type: 'setVehicle', vehicle })
                }
                dispatch({ type: 'setQuoteInfos', quoteInfos })

                dispatch({ type: 'fetchSucceed' })
            } catch (e) {
                console.error(e)
            }
        }

        if (state.jwt && state.quoteId) fetchQuoteDesking()
    }, [state.jwt, state.quoteId])

    // Init payment options info in State
    React.useEffect(() => {
        const fetchPaymentOptions = async () => {
            try {
                dispatch({ type: 'fetching' })

                const paramsPaymentOptions = {
                    quoteId: state.quoteId || '',
                    lang,
                    location,
                    orgId: parseInt(orgId),
                    orgUnitId: parseInt(orgUnitId),
                    payload: {
                        transactionToken: state.vehicle?.paymentOptions?.transactionToken || state.quoteInfos.transactionToken,
                        saleType: state.quoteInfos.saleType,
                        taxPlan: state.quoteInfos.taxPlan,
                        vehicleCondition: state.quoteInfos.vehicleCondition,
                        odometer: state.vehicle.odometer,
                        purchaseMethod: selectedPurchaseMethod || state.quoteInfos.purchaseMethod,
                        financePlan: state.quoteInfos.financePlan,
                        kmPerYearPlan: parseInt(selectedKmPerYearPlan) || state.quoteInfos.kmPerYearPlan,
                        paymentFrequency: parseInt(selectedFrequency) || state.quoteInfos.paymentFrequency,
                        term: parseInt(selectedTerm) || state.quoteInfos.term,
                        cashDown: state.quoteInfos.cashDown,
                        tradeIn: state.quoteInfos.tradeIn,
                        lien: state.quoteInfos.lien,
                        financeOptions: state.quoteInfos.financeOptions,
                    },
                    token: state.jwt,
                }

                const initPaymentOptions = await getQuoteDeskingPaymentOptions(paramsPaymentOptions)
                dispatch({ type: 'setPaymentOptions', paymentOptions: initPaymentOptions.data })

                dispatch({ type: 'fetchSucceed' })
            } catch (error) {
                console.error(error)
            }
        }

        if (
            state.jwt &&
            Object.entries(state.quoteInfos).length &&
            Object.entries(state.vehicle).length &&
            !Object.entries(state.paymentOptions).length
        )
            fetchPaymentOptions()
    }, [state.jwt, state.quoteInfos, state.vehicle, state.paymentOptions])

    // Event listener when added to garage / quote update
    // React.useEffect(() => {
    //     if (WindowProxy) {
    //         WindowProxy.document.addEventListener('sm360.widgets.garage.added', () =>
    //             fetchShowroomQuote(state.quoteId, state.jwt)
    //         )
    //     }
    // }, [])

    const fetchShowroomQuote = async () => {
        const initJwt = cookie || (await getJwt())
        const quoteId = getParamValue('quoteId')
        try {
            dispatch({ type: 'fetching' })

            const params = {
                quoteId,
                lang,
                location,
                orgId: parseInt(orgId),
                orgUnitId: parseInt(orgUnitId),
                token: initJwt,
            }

            const quote = await getQuoteDesking(params)
            const { vehicle, quoteInfos } = quoteDeskingTransform(quote.data)

            dispatch({ type: 'setVehicle', vehicle })
            dispatch({ type: 'setQuoteInfos', quoteInfos })

            dispatch({ type: 'fetchSucceed' })
        } catch (e) {
            console.error(e)
        }
    }

    return <Context.Provider value={{ state, dispatch }}>{children}</Context.Provider>
}

Provider.propTypes = {
    lang: PropTypes.string,
    location: PropTypes.string,
    orgId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    orgUnitId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quoteId: PropTypes.string,
    vehicleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    newCarTrimId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vehicleInventoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hasShowroomWidgets: PropTypes.bool,
    calculatorCtaVersion: PropTypes.string,
    vehicleCategory: PropTypes.string,
    isStandaloneVLP: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    selectedPurchaseMethod: PropTypes.string,
    selectedTerm: PropTypes.number,
    selectedFrequency: PropTypes.number,
    selectedKmPerYearPlan: PropTypes.number,
}

Provider.defaultProps = {
    lang: '',
    location: '',
    orgId: null,
    orgUnitId: null,
    quoteId: '',
    vehicleId: null,
    newCarTrimId: null,
    vehicleInventoryId: null,
    hasShowroomWidgets: false,
    calculatorCtaVersion: 'primary',
    vehicleCategory: 'inventory',
    selectedPurchaseMethod: '',
    selectedTerm: 0,
    selectedFrequency: 0,
    selectedKmPerYearPlan: 0,
}

export { Context, Provider }
